import { useEffect, useRef } from 'react';
import { CursorPosition } from '../../../contexts/Cursor/cursorReducer';
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../../helpers/featureFlags';
import { NodeGetter } from './getNode';
import { ConverterContext, selectRenderState } from './selectRenderState';
import type { RenderStateDeliverer } from './deliverRenderState';

// console.log output is temporary; replace with the real render-state channel once it lands.
export function useRenderStateSubscriber(
  cursor: CursorPosition | null,
  nodeGetter: NodeGetter,
  ctx: ConverterContext,
  fetchEtalon: ((branchId: number) => Promise<unknown>) | null,
  deliver: RenderStateDeliverer | null = null,
): void {
  const lastFrameRef = useRef<{ node: object; stepIndex: number | null } | null>(null);

  useEffect(() => {
    if (!cursor) {
      return;
    }
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) {
      return;
    }

    const renderState = selectRenderState(cursor, nodeGetter, ctx);

    if (!renderState) {
      return;
    }

    const last = lastFrameRef.current;
    if (last && last.node === renderState.node && last.stepIndex === renderState.stepIndex) {
      return;
    }
    lastFrameRef.current = { node: renderState.node, stepIndex: renderState.stepIndex };

    if (typeof window !== 'undefined' && (window as typeof window & { __CURSOR_DEBUG__?: boolean }).__CURSOR_DEBUG__) {
      // eslint-disable-next-line no-console
      console.log('[render-state]', renderState);
    }

    if (deliver !== null) {
      deliver(renderState);
    }

    // Enable diff tool: localStorage.setItem('featureEmulatorFrameDiff', '1')
    if (
      process.env.NODE_ENV !== 'production' &&
      fetchEtalon !== null &&
      renderState &&
      typeof window !== 'undefined' &&
      localStorage.getItem('featureEmulatorFrameDiff') === '1'
    ) {
      import('../../../components/utils/objectDiff').then(({ diffObjects }) => {
        const IGNORE_PATHS = [
          '[steps][*][stepAudios][*][Audio][audioUrl]', // bucket URL differs by region in dev vs prod
          '[steps][*][character][imageUrl]',            // host-prefix differs between EC context and server getBucketUrl()
          // policyId is a server-only DB column; EC AudiosProvider type does not carry it
          // and the renderer does not consume it
          '[steps][*][stepAudios][*][Audio][policyId]',
          // metadata timestamps from the server serializer; the renderer does not consume them
          // and the client expressions list does not carry them
          '[steps][*][expression][createdAt]',
          '[steps][*][expression][updatedAt]',
          '[location]',                                 // emulator resolves this itself via HTTP
          '[links_from]',                               // not consumed by emulator renderer
          '[links_to]',                                 // not consumed by emulator renderer
          // answers[*].uuid: getAllV3 rowToAnswer.getData() explicitly excludes uuid
          // (getBranchesRaw.js:774-781, comment "Match v1 step.answers: no uuid").
          // CT AnswerStep type does not declare uuid; renderer does not consume it.
          '[steps][*][answers][*][uuid]',
          // Audio.alias/disabled: absent from /v1/audio/book/:bookId SQL select
          // (audio.js:370-391 selects only id/title/type/userId/duration/audioName).
          // CT BranchStep does not declare stepAudios; renderer does not consume these fields.
          '[steps][*][stepAudios][*][Audio][alias]',
          '[steps][*][stepAudios][*][Audio][disabled]',
          // Audio.createdAt/updatedAt: same source gap as alias/disabled above;
          // server serializer (public_preview.js Sequelize include) returns full Audio row,
          // book audio endpoint does not.
          '[steps][*][stepAudios][*][Audio][createdAt]',
          '[steps][*][stepAudios][*][Audio][updatedAt]',
        ];
        fetchEtalon(cursor.branchId)
          .then((etalon) => {
            const report = diffObjects(etalon, renderState.node, { ignorePaths: IGNORE_PATHS });

            if (report.suppressedEntries.length > 0) {
              // eslint-disable-next-line no-console
              console.groupCollapsed(
                `[render-state-diff] suppressed by skip-list: ${report.suppressedEntries.length}`,
              );
              // eslint-disable-next-line no-console
              console.table(report.suppressedEntries);
              // eslint-disable-next-line no-console
              console.groupEnd();
            }

            if (report.hasDiff) {
              // eslint-disable-next-line no-console
              console.table(report.entries);
            } else {
              // eslint-disable-next-line no-console
              console.log('[render-state-diff] no diff for branchId', cursor.branchId);
            }
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.warn('[render-state-diff] etalon fetch failed', e);
          });
      });
    }
  // lastFrameRef.current is mutated in place — adding it to deps would defeat dedup.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, nodeGetter, ctx, fetchEtalon, deliver]);
}
